import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Camera, Users, Globe, ArrowRight } from "lucide-react";
import { useTheme } from "../../context/ThemeContext";
import { useTranslation } from "react-i18next";

function HeroLanding() {
  const { theme } = useTheme();
  const { t } = useTranslation();

  return (
    <div className="h-full overflow-y-auto bg-th-bg">
      {/* Hero section */}
      <div className="relative flex flex-col items-center justify-center min-h-[80vh] px-6 text-center">
        {/* Subtle background glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 rounded-full bg-th-accent opacity-[0.06] blur-3xl" />
        </div>

        <div className="relative z-10 animate-fadeInUp">
          <img
            src={theme === "dark" ? "/pinned-logo-light-stacked.png" : "/pinned-logo-dark-stacked.png"}
            alt="Pinned"
            className="w-48 mx-auto mb-4"
          />
          <p className="text-th-accent text-sm font-mono uppercase tracking-widest mb-6">
            {t("app.tagline")}
          </p>
          <h1 className="text-3xl font-bold text-th-text leading-tight max-w-md mx-auto">
            {t("landing.headline")}
          </h1>
          <p className="text-th-text-sec mt-3 max-w-sm mx-auto text-sm leading-relaxed">
            {t("landing.subheadline")}
          </p>

          <div className="flex flex-col gap-3 mt-8 w-full max-w-xs mx-auto">
            <Link
              to="/register"
              className="flex items-center justify-center gap-2 w-full py-3.5 bg-th-accent text-white rounded-xl text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all shadow-lg shadow-emerald-500/20"
            >
              {t("landing.cta")}
              <ArrowRight size={16} />
            </Link>
            <Link
              to="/explore"
              className="w-full py-3 bg-th-surface border border-th-border text-th-text-sec rounded-xl text-sm font-medium hover:text-th-text transition-colors"
            >
              {t("landing.exploreCities")}
            </Link>
          </div>
        </div>
      </div>

      {/* Value props */}
      <div className="px-6 pb-12 max-w-md mx-auto">
        <div className="space-y-4 stagger">
          <ValueCard
            icon={<MapPin size={20} />}
            title={t("landing.feature1Title")}
            desc={t("landing.feature1Desc")}
          />
          <ValueCard
            icon={<Camera size={20} />}
            title={t("landing.feature2Title")}
            desc={t("landing.feature2Desc")}
          />
          <ValueCard
            icon={<Users size={20} />}
            title={t("landing.feature3Title")}
            desc={t("landing.feature3Desc")}
          />
          <ValueCard
            icon={<Globe size={20} />}
            title={t("landing.feature4Title")}
            desc={t("landing.feature4Desc")}
          />
        </div>

        <div className="mt-10 text-center">
          <Link
            to="/register"
            className="inline-flex items-center gap-2 px-6 py-3 bg-th-accent text-white rounded-xl text-sm font-semibold hover:opacity-90 active:scale-[0.98] transition-all"
          >
            {t("landing.ctaBottom")}
            <ArrowRight size={16} />
          </Link>
          <p className="text-[11px] text-th-muted mt-3 font-mono">
            {t("landing.noSpam")}
          </p>
        </div>
      </div>
    </div>
  );
}

function ValueCard({ icon, title, desc }) {
  return (
    <div className="flex gap-4 p-4 bg-th-card border border-th-border rounded-xl">
      <div className="w-10 h-10 rounded-xl bg-th-accent-dim text-th-accent flex items-center justify-center flex-shrink-0">
        {icon}
      </div>
      <div>
        <h3 className="text-sm font-semibold text-th-text">{title}</h3>
        <p className="text-xs text-th-text-sec mt-0.5 leading-relaxed">{desc}</p>
      </div>
    </div>
  );
}

export default HeroLanding;
